'use client';

import * as React from "react";
import { Code2, Maximize2, Minimize2, X } from "lucide-react";
import { cn } from "../../utils/cn";

/**
 * MiniIDE — Standard-mode lab-into-lecture bidirectional companion.
 *
 * MV5 Figma page `24 · Peek + Mini-IDE · MV5` (`127:56`). Five states from
 * the M1-M5 spec:
 *
 *   M1 chip · resting          — the "open the code" affordance in the
 *                                lecture footer; a compact chip with the
 *                                snippet's language + file name.
 *   M2 overlay compact         — a small edit surface (~640×360) so the
 *                                learner can try the snippet without leaving
 *                                the lecture.
 *   M3 overlay expanded        — larger centered overlay (~900×540) with
 *                                room for a mini file tree.
 *   M4 full-screen takeover    — the mini-IDE takes over; equivalent to
 *                                jumping into Studio mode temporarily.
 *   M5 not-supported chip      — the current surface has no code snippet
 *                                (a reading, an assessment, or a lecture
 *                                without a linked snippet); disabled state.
 *
 * Peek + Mini-IDE mirror each other structurally. Interaction matrix H2
 * says: only one overlay open at a time. When the caller (Learning Item
 * Shell) sees this transition from `chip` → an overlay state, it should
 * fold the sibling PeekPlayer back to `chip`. This component doesn't
 * enforce that itself.
 */

export type MiniIDEState = "chip" | "compact" | "expanded" | "full" | "empty";

export interface MiniIDEProps {
  state: MiniIDEState;
  onStateChange: (next: MiniIDEState) => void;
  /** Snippet title; typically the file name (e.g. `train.py`). */
  title?: string;
  /** Optional language chip content (e.g. "Python", "TypeScript"). */
  language?: string;
  /** Slot: the actual editor element (Monaco, CodeMirror, plain <pre>). */
  renderEditor?: (state: Exclude<MiniIDEState, "chip" | "empty">) => React.ReactNode;
  /** Chip click handler. Defaults to transitioning to `compact`. */
  onOpen?: () => void;
  /** Optional Run handler shown in the overlay footer. */
  onRun?: () => void;
  /** Whether to render as a mobile-optimized bottom sheet. */
  mobile?: boolean;
  /** Custom label for the empty/not-supported chip. */
  emptyLabel?: string;
  className?: string;
}

const OVERLAY_STATES: MiniIDEState[] = ["compact", "expanded", "full"];

function isOverlay(s: MiniIDEState): s is "compact" | "expanded" | "full" {
  return OVERLAY_STATES.includes(s);
}

// ── M1/M5 Chip ─────────────────────────────────────────────────────────────
const MiniIDEChip: React.FC<{
  title?: string;
  language?: string;
  disabled?: boolean;
  onClick?: () => void;
  emptyLabel?: string;
}> = ({ title, language, disabled, onClick, emptyLabel }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    aria-disabled={disabled}
    disabled={disabled}
    data-slot="mini-ide-chip"
    data-state={disabled ? "empty" : "resting"}
    className={cn(
      "inline-flex items-center gap-space-8 h-[32px] px-space-12 rounded-chip",
      "border text-caption-12 font-medium",
      "transition-colors duration-micro ease-standard",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-violet",
      disabled
        ? "border-chrome-border bg-chrome-panel text-ink-muted cursor-not-allowed"
        : "border-accent-violet/40 bg-accent-violet/10 text-ink-primary hover:bg-accent-violet/20",
    )}
  >
    <Code2 className="size-4 shrink-0" />
    <span className="max-w-[200px] truncate font-mono">
      {disabled ? emptyLabel ?? "No code snippet" : title ?? "Try the code"}
    </span>
    {!disabled && language && (
      <span
        className={cn(
          "px-space-4 py-[1px] rounded-chip text-[10px] font-medium",
          "bg-chrome-surface text-ink-muted",
        )}
      >
        {language}
      </span>
    )}
  </button>
);

// ── M2 / M3 / M4 Overlay ───────────────────────────────────────────────────
const MiniIDEOverlay: React.FC<{
  state: "compact" | "expanded" | "full";
  onStateChange: (next: MiniIDEState) => void;
  title?: string;
  language?: string;
  renderEditor?: (state: "compact" | "expanded" | "full") => React.ReactNode;
  onRun?: () => void;
  mobile?: boolean;
}> = ({ state, onStateChange, title, language, renderEditor, onRun, mobile }) => {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (state === "full") onStateChange("expanded");
      else onStateChange("chip");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, onStateChange]);

  const sizeClass: Record<typeof state, string> = {
    compact: mobile
      ? "fixed inset-x-0 bottom-0 h-[45vh] rounded-t-card"
      : "fixed bottom-space-24 start-space-24 w-[640px] max-w-[calc(100vw-48px)] h-[360px] rounded-card shadow-2xl",
    expanded: mobile
      ? "fixed inset-x-0 bottom-0 h-[70vh] rounded-t-card"
      : "fixed inset-0 m-auto w-[900px] max-w-[calc(100vw-64px)] h-[540px] max-h-[calc(100vh-64px)] rounded-card shadow-2xl",
    full: "fixed inset-0 rounded-none",
  };

  const nextExpand: MiniIDEState = state === "compact" ? "expanded" : "full";
  const nextShrink: MiniIDEState =
    state === "full" ? "expanded" : state === "expanded" ? "compact" : "compact";

  return (
    <div
      role="dialog"
      aria-label={title ? `Mini-IDE: ${title}` : "Mini-IDE"}
      data-slot="mini-ide-overlay"
      data-state={state}
      className={cn(
        "z-[60] bg-chrome-surface border border-chrome-border",
        "flex flex-col overflow-hidden",
        sizeClass[state],
      )}
    >
      {/* Header — always visible on Mini-IDE (unlike PeekPlayer P2) because
          the editor needs its filename bar as a persistent affordance. */}
      <div
        className={cn(
          "flex items-center justify-between px-space-12 py-space-8 border-b border-chrome-border",
          "bg-chrome-panel",
        )}
      >
        <div className="flex items-center gap-space-8 min-w-0 flex-1">
          <Code2 className="size-4 text-ink-muted shrink-0" />
          <p className="text-caption-12 font-mono text-ink-primary truncate">
            {title ?? "snippet"}
          </p>
          {language && (
            <span className="px-space-4 py-[1px] rounded-chip text-[10px] font-medium bg-chrome-surface text-ink-muted shrink-0">
              {language}
            </span>
          )}
        </div>
        <div className="flex items-center gap-space-4 shrink-0">
          {state !== "compact" && (
            <button
              type="button"
              onClick={() => onStateChange(nextShrink)}
              aria-label={state === "full" ? "Restore" : "Shrink to compact"}
              className="p-space-4 rounded-chip text-ink-muted hover:text-ink-primary hover:bg-chrome-surface"
            >
              <Minimize2 className="size-4" />
            </button>
          )}
          {state !== "full" && (
            <button
              type="button"
              onClick={() => onStateChange(nextExpand)}
              aria-label={state === "compact" ? "Expand" : "Full screen"}
              className="p-space-4 rounded-chip text-ink-muted hover:text-ink-primary hover:bg-chrome-surface"
            >
              <Maximize2 className="size-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onStateChange("chip")}
            aria-label="Close mini-IDE"
            className="p-space-4 rounded-chip text-ink-muted hover:text-ink-primary hover:bg-chrome-surface"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 bg-[color:var(--ide-surface,#1e1e1e)]">
        {renderEditor ? (
          renderEditor(state)
        ) : (
          <div className="w-full h-full flex items-center justify-center text-caption-12 text-ink-muted">
            No editor rendered
          </div>
        )}
      </div>

      {onRun && (
        <div className="flex items-center justify-end gap-space-8 px-space-12 py-space-8 border-t border-chrome-border bg-chrome-panel">
          <button
            type="button"
            onClick={onRun}
            className={cn(
              "inline-flex items-center gap-space-4 h-[28px] px-space-12 rounded-chip",
              "bg-accent-violet text-white text-caption-12 font-medium",
              "hover:bg-accent-violet/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-violet",
            )}
          >
            Run
          </button>
        </div>
      )}
    </div>
  );
};

export const MiniIDE = React.forwardRef<HTMLDivElement, MiniIDEProps>(
  function MiniIDE(
    { state, onStateChange, title, language, renderEditor, onOpen, onRun, mobile, emptyLabel, className },
    ref,
  ) {
    const openChip = React.useCallback(() => {
      onOpen ? onOpen() : onStateChange("compact");
    }, [onOpen, onStateChange]);

    if (state === "chip" || state === "empty") {
      return (
        <div ref={ref} className={className} data-slot="mini-ide" data-state={state}>
          <MiniIDEChip
            title={title}
            language={language}
            disabled={state === "empty"}
            onClick={openChip}
            emptyLabel={emptyLabel}
          />
        </div>
      );
    }

    if (isOverlay(state)) {
      return (
        <div ref={ref} className={className} data-slot="mini-ide" data-state={state}>
          <MiniIDEOverlay
            state={state}
            onStateChange={onStateChange}
            title={title}
            language={language}
            renderEditor={renderEditor}
            onRun={onRun}
            mobile={mobile}
          />
        </div>
      );
    }

    return null;
  },
);

MiniIDE.displayName = "MiniIDE";
